import { useCallback, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import type { GridApi } from 'ag-grid-community';
import { LoaderCircleIcon } from 'lucide-react';
import { type FieldErrors, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetBreedsQuery } from '@/app/services/lookups';
import { Button } from '@/components/ui/button';
import { ConfirmProvider, useConfirm } from '@/components/ui/confirm-dialog';
import {
  type DonorMismatchData,
  DonorMismatchDialog,
} from '@/pages/bulk-donor-scheduling/components/donor-mismatch-dialog';
import { FertilizationDropdown } from '@/pages/bulk-donor-scheduling/components/fertilization-dropdown';
import {
  type ApplyAgllcPriorityInput,
  type UpsertAgllcBulkDonorScheduleRequest,
  useApplyAgllcPriorities,
  useUpsertAgllcBulkDonorSchedules,
  useValidateAgllcBulkDonorSchedules,
  type ValidateAgllcBulkDonorScheduleRequest,
  type ValidateAgllcBulkDonorScheduleResponse,
  ValidationMessageLevel,
} from '@/services/bulk-donor-schedule';
import { isAbortError } from '@/services/progress-stream';

import { useAgllcBulkDonorScheduleStore } from './agllc/hooks/use-agllc-bulk-donor-schedule-store';
import { useAgllcColumns } from './agllc/hooks/use-agllc-columns';
import { parseAgllcRoster } from './agllc/utils/agllc-roster-parser';
import {
  type AgllcBulkDonorScheduleFieldsInput,
  AgllcBulkDonorScheduleFieldsSchema,
  type AgllcBulkDonorScheduleInput,
  AgllcBulkDonorScheduleRowsSchema,
} from './agllc/utils/schema';
import { BulkDonorScheduleGrid } from './components/bulk-donor-schedule-grid';
import { UnsavedChangesGuard } from './components/unsaved-changes-guard';
import { type Messages } from './hooks/use-bulk-donor-schedule-store';
import { useNotifications } from './hooks/use-notifications';

const normalizeBreedValue = (value: string) => value.trim().toLowerCase();
const AGLLC_DEFAULT_FERTILIZATION_SITE_ID =
  '8e229164-4c97-de11-8de3-0019d18a6560';

type PendingDonorMismatch = {
  rowIndex: number;
  donorName: string;
  mismatchData: DonorMismatchData;
};

type AgllcDonorResolution = 'USE_EXISTING' | 'CREATE_NEW' | 'UPDATE_EXISTING';
type AgllcGridRow = AgllcBulkDonorScheduleInput;

export function AgllcBulkDonorSchedulePage() {
  return (
    <ConfirmProvider>
      <AgllcBulkDonorSchedulePageContent />
    </ConfirmProvider>
  );
}

function AgllcBulkDonorSchedulePageContent() {
  const { t } = useTranslation();
  const confirm = useConfirm();

  const [state, dispatch] = useAgllcBulkDonorScheduleStore();
  const { data: bovineBreeds } = useGetBreedsQuery({});

  const { control, handleSubmit, reset, getValues } =
    useForm<AgllcBulkDonorScheduleFieldsInput>({
      resolver: zodResolver(AgllcBulkDonorScheduleFieldsSchema),
      defaultValues: {
        fertilizationSiteId: AGLLC_DEFAULT_FERTILIZATION_SITE_ID,
      },
    });

  const [api, setApi] = useState<GridApi<AgllcGridRow>>();
  const [isGridEditing, setGridEditing] = useState(false);
  const [donorResolutions, setDonorResolutions] = useState<
    Record<number, AgllcDonorResolution>
  >({});
  const [pendingDonorMismatches, setPendingDonorMismatches] = useState<
    PendingDonorMismatch[]
  >([]);
  const [mismatchDialog, setMismatchDialog] = useState<{
    visible: boolean;
    rowIndex: number | null;
    donorName: string;
    mismatchData: DonorMismatchData | null;
  }>({
    visible: false,
    rowIndex: null,
    donorName: '',
    mismatchData: null,
  });

  const notifications = useNotifications();

  const validBovineBreedValues = useMemo(() => {
    const values = new Set<string>();
    for (const breed of bovineBreeds || []) {
      if (breed?.code) {
        values.add(normalizeBreedValue(breed.code));
      }
      if (breed?.name) {
        values.add(normalizeBreedValue(breed.name));
      }
    }
    return values;
  }, [bovineBreeds]);

  const columns = useAgllcColumns();

  const validateSchedule = useValidateAgllcBulkDonorSchedules();
  const upsertSchedulesMutation = useUpsertAgllcBulkDonorSchedules();

  const handleValidate = useCallback(
    async (fields: AgllcBulkDonorScheduleFieldsInput) => {
      if (!api) {
        return;
      }

      dispatch({ type: 'client_validating' });
      api.stopEditing();

      const rows = state.rows.map((row) => {
        if (!row || Object.values(row).filter((v) => !!v).length === 0) {
          return undefined;
        }
        return row;
      });

      const rowsResult = AgllcBulkDonorScheduleRowsSchema.safeParse(rows);

      const inputs = rowsResult.data?.filter(
        (row): row is AgllcBulkDonorScheduleInput => row !== null,
      );

      if (!rowsResult.success || !inputs?.length) {
        const errors: Record<string, string> = {};

        const colId = api
          .getColumns()
          ?.find((c) => c.getColDef().field)
          ?.getColDef().field;
        if (!inputs?.length && colId) {
          errors[`${0}.${colId}`] = 'At least one valid row is required';
        }

        if (rowsResult.error?.issues) {
          for (const issue of rowsResult.error.issues) {
            const rowIndex = issue.path[0] as string;
            const colId = issue.path[1] as string;
            errors[`${rowIndex}.${colId}`] = issue.message;
          }
        }

        dispatch({ type: 'client_validated', errors });
        notifications.validationErrors();
        return;
      }

      if (bovineBreeds?.length) {
        const breedErrors: Record<string, string> = {};
        rowsResult.data.forEach((row, rowIndex) => {
          if (!row) {
            return;
          }

          const donorBreed = row.donorBreed?.trim();
          if (
            donorBreed &&
            !validBovineBreedValues.has(normalizeBreedValue(donorBreed))
          ) {
            breedErrors[`${rowIndex}.donorBreed`] =
              'Breed must be a valid bovine breed code or name';
          }
        });

        if (Object.keys(breedErrors).length > 0) {
          dispatch({ type: 'client_validated', errors: breedErrors });
          notifications.validationErrors();
          return;
        }
      }

      dispatch({ type: 'client_validated', errors: undefined });

      try {
        const variables: ValidateAgllcBulkDonorScheduleRequest = {
          fertilizationSiteId: fields.fertilizationSiteId,
          inputs: rowsResult.data,
        };

        const result: ValidateAgllcBulkDonorScheduleResponse =
          await validateSchedule.mutateAsync(variables);

        if (result.duplicateIndexes.length > 0) {
          const duplicateRows = result.duplicateIndexes.map(
            (i) => state.rows[i],
          );
          confirm({
            title: t('bulkDonorScheduling.duplicateRosterHeader'),
            body: (
              <div>
                <p>
                  {t('bulkDonorScheduling.duplicateRosterMessage', {
                    duplicateCount: result.duplicateIndexes.length,
                    totalCount: state.rows.filter(Boolean).length,
                    accountName: 'AGLLC',
                  })}
                </p>
                <ul className="mt-2 list-disc pl-5">
                  {duplicateRows.map((row, idx) => (
                    <li key={idx}>
                      {row?.donorNameToMatch ?? '?'} - {row?.opuDate ?? 'N/A'}
                    </li>
                  ))}
                </ul>
              </div>
            ),
            confirmLabel: t('common.yes'),
            cancelLabel: t('common.no'),
            onConfirm: () => {},
          });
        }

        let messages: Messages | undefined = undefined;
        let errors: Record<string, string> | undefined = undefined;

        for (const validation of result.fields) {
          const field = validation.field;
          if (!messages) {
            messages = {};
          }
          messages[field] = {
            level: ValidationMessageLevel.MESSAGE,
            message: validation.message,
          };
        }

        const unresolvedDonorMismatches: PendingDonorMismatch[] = [];
        for (const validation of result.inputs) {
          const rowIndex = validation.index.toString();
          const colId = validation.field;

          if (
            validation.correctedValue &&
            validation.level === ValidationMessageLevel.MESSAGE
          ) {
            dispatch((state) => {
              const updatedRow = {
                ...state.rows[validation.index],
                [colId]: validation.correctedValue,
              };
              return {
                type: 'set_row',
                rowIndex: validation.index,
                data: updatedRow,
              };
            });
          }

          if (validation.level === ValidationMessageLevel.ERROR) {
            if (!errors) {
              errors = {};
            }
            errors[`${rowIndex}.${colId}`] = validation.message;
          } else {
            if (!messages) {
              messages = {};
            }
            messages[`${rowIndex}.${colId}`] = {
              level: validation.level,
              message: validation.message,
              suggestions: validation.suggestions || undefined,
            };

            if (
              validation.level === ValidationMessageLevel.WARNING &&
              validation.mismatchData &&
              !donorResolutions[validation.index]
            ) {
              const row = state.rows[validation.index];
              unresolvedDonorMismatches.push({
                rowIndex: validation.index,
                donorName: row?.donorNameToMatch || '',
                mismatchData: validation.mismatchData,
              });
            }
          }
        }

        if (errors) {
          setPendingDonorMismatches([]);
          notifications.validationErrors();
        } else if (unresolvedDonorMismatches.length > 0) {
          setPendingDonorMismatches(unresolvedDonorMismatches);
          const mismatch = unresolvedDonorMismatches[0];
          setMismatchDialog({
            visible: true,
            rowIndex: mismatch.rowIndex,
            donorName: mismatch.donorName,
            mismatchData: mismatch.mismatchData,
          });
        } else {
          setPendingDonorMismatches([]);
          notifications.validationSuccessful();
        }

        dispatch({ type: 'server_validated', errors, messages });
      } catch (error) {
        if (isAbortError(error)) {
          dispatch({ type: 'request_cancelled' });
          return;
        }
        notifications.validationServerError(error);
        dispatch({ type: 'server_error' });
        console.error('Error while validating data', error);
      }
    },
    [
      api,
      confirm,
      dispatch,
      notifications,
      state.rows,
      t,
      validateSchedule,
      bovineBreeds,
      validBovineBreedValues,
      donorResolutions,
    ],
  );

  const handleInvalidSubmit = useCallback(
    (_errors: FieldErrors<AgllcBulkDonorScheduleFieldsInput>) => {
      notifications.validationErrors();
    },
    [notifications],
  );

  const handleUpload = useCallback(async () => {
    if (!api) {
      return;
    }

    dispatch({ type: 'uploading' });

    try {
      const fields = getValues();
      const parsedRows = AgllcBulkDonorScheduleRowsSchema.parse(
        state.rows.map((row) =>
          row && Object.values(row).filter((v) => !!v).length ? row : null,
        ),
      );
      const rowsWithIndexes = parsedRows.flatMap((row, rowIndex) =>
        row
          ? [
              {
                row: {
                  ...row,
                  donorResolution:
                    (donorResolutions[rowIndex] === 'UPDATE_EXISTING'
                      ? 'USE_EXISTING'
                      : donorResolutions[rowIndex]) ?? row.donorResolution,
                },
                rowIndex,
              },
            ]
          : [],
      );

      const variables: UpsertAgllcBulkDonorScheduleRequest = {
        fertilizationSiteId: fields.fertilizationSiteId,
        inputs: rowsWithIndexes.map(({ row }) => row),
      };

      const result = await upsertSchedulesMutation.mutateAsync(variables);

      const warningRowIndexes = new Set(
        result.manualFollowUpInputIndexes.flatMap((inputIndex) => {
          const rowIndex = rowsWithIndexes[inputIndex]?.rowIndex;
          return rowIndex === undefined ? [] : [rowIndex];
        }),
      );

      notifications.processingSuccessful({
        donorSchedulesProcessed: result.donorSchedulesProcessed,
        soSchedulesCreated: result.soSchedulesCreated,
        chuteSchedulesCreated: result.chuteSchedulesCreated,
        successfulRows: result.successfulInputIndexes.length,
        manualFollowUpRows: result.manualFollowUpInputIndexes.length,
      });

      dispatch({ type: 'success', warningRowIndexes });
    } catch (error) {
      if (isAbortError(error)) {
        dispatch({ type: 'request_cancelled' });
        return;
      }
      notifications.uploadServerError(error);
      dispatch({ type: 'server_error' });
      console.error('Error while uploading data', error);
    }
  }, [
    api,
    dispatch,
    donorResolutions,
    getValues,
    notifications,
    state.rows,
    upsertSchedulesMutation,
  ]);

  const handleRowChanged = useCallback(
    (rowIndex: number, data: AgllcGridRow, field: string) => {
      if (
        field === 'donorNameToMatch' ||
        field === 'donorRegistrationNumber' ||
        field === 'birthDate'
      ) {
        setDonorResolutions((current) => {
          if (current[rowIndex] === undefined) return current;
          const next = { ...current };
          delete next[rowIndex];
          return next;
        });
      }

      dispatch({ type: 'set_row', rowIndex, data });
    },
    [dispatch],
  );

  const resolveCurrentDonorMismatch = useCallback(
    (action: AgllcDonorResolution) => {
      if (mismatchDialog.rowIndex === null || !mismatchDialog.mismatchData) {
        return;
      }

      setDonorResolutions((current) => ({
        ...current,
        [mismatchDialog.rowIndex!]: action,
      }));

      if (action === 'USE_EXISTING') {
        const rowIndex = mismatchDialog.rowIndex;
        const currentRow = state.rows[rowIndex];
        if (currentRow) {
          const updatedRows = [...state.rows];
          updatedRows[rowIndex] = {
            ...currentRow,
            donorRegistrationNumber:
              mismatchDialog.mismatchData.existingRegistrationNumber ||
              currentRow.donorRegistrationNumber,
            birthDate:
              mismatchDialog.mismatchData.existingBirthDate ||
              currentRow.birthDate,
          };
          dispatch({ type: 'set_rows', rows: updatedRows });
        }
      }

      const msgKey = `${mismatchDialog.rowIndex}.donorNameToMatch`;
      const updatedMessages = { ...state.messages };
      if (action === 'UPDATE_EXISTING') {
        updatedMessages[msgKey] = {
          level: ValidationMessageLevel.MESSAGE,
          message: `Donor "${mismatchDialog.donorName}" will be updated with the uploaded data.`,
        };
      } else if (action === 'USE_EXISTING') {
        updatedMessages[msgKey] = {
          level: ValidationMessageLevel.MESSAGE,
          message: `Donor "${mismatchDialog.donorName}" will use existing data from the database.`,
        };
      } else if (action === 'CREATE_NEW') {
        updatedMessages[msgKey] = {
          level: ValidationMessageLevel.MESSAGE,
          message: `A new donor record will be created for "${mismatchDialog.donorName}".`,
        };
      }
      dispatch({
        type: 'server_validated',
        errors: undefined,
        messages: updatedMessages,
      });

      const remaining = pendingDonorMismatches.filter(
        (m) => m.rowIndex !== mismatchDialog.rowIndex,
      );
      setPendingDonorMismatches(remaining);
      const nextMismatch = remaining[0];
      setMismatchDialog(
        nextMismatch
          ? {
              visible: true,
              rowIndex: nextMismatch.rowIndex,
              donorName: nextMismatch.donorName,
              mismatchData: nextMismatch.mismatchData,
            }
          : {
              visible: false,
              rowIndex: null,
              donorName: '',
              mismatchData: null,
            },
      );

      if (!nextMismatch) {
        notifications.validationSuccessful();
      }
    },
    [
      mismatchDialog,
      notifications,
      pendingDonorMismatches,
      state.rows,
      state.messages,
      dispatch,
    ],
  );

  const handleUseExistingData = useCallback(() => {
    resolveCurrentDonorMismatch('USE_EXISTING');
  }, [resolveCurrentDonorMismatch]);

  const handleCreateNewDonor = useCallback(() => {
    resolveCurrentDonorMismatch('CREATE_NEW');
  }, [resolveCurrentDonorMismatch]);

  const handleUpdateExistingDonor = useCallback(() => {
    resolveCurrentDonorMismatch('UPDATE_EXISTING');
  }, [resolveCurrentDonorMismatch]);

  const applyAgllcPrioritiesMutation = useApplyAgllcPriorities();

  const handleCalculatePriorities = useCallback(async () => {
    const inputs = state.rows
      .map((row, index) => {
        if (!row) {
          return null;
        }
        return {
          index,
          opuDate: row.opuDate ?? null,
          donorPsi: row.psi ?? null,
          sir1Psi: row.sir1Psi ?? null,
          reg: row.donorRegistrationNumber ?? null,
          donorNameToMatch: row.donorNameToMatch ?? null,
        } satisfies ApplyAgllcPriorityInput as ApplyAgllcPriorityInput;
      })
      .filter((input): input is ApplyAgllcPriorityInput => input !== null);

    try {
      const result = await applyAgllcPrioritiesMutation.mutateAsync({
        inputs,
      });
      const updatedRows = [...state.rows];
      for (const p of result.priorities) {
        const row = updatedRows[p.index];
        if (!row) {
          continue;
        }
        updatedRows[p.index] = { ...row, priority: p.priority };
      }
      dispatch({ type: 'set_rows', rows: updatedRows });
      notifications.prioritiesCalculated(result);
    } catch (error) {
      console.error('Error calculating priorities', error);
      notifications.validationServerError(error);
    }
  }, [state.rows, dispatch, applyAgllcPrioritiesMutation, notifications]);

  const handleCancelMismatch = useCallback(() => {
    setPendingDonorMismatches([]);
    setMismatchDialog({
      visible: false,
      rowIndex: null,
      donorName: '',
      mismatchData: null,
    });
  }, []);

  const processFileUpload = useCallback(
    async (file: File) => {
      try {
        const roster = await parseAgllcRoster(file);

        if (roster.rows.length === 0) {
          notifications.fileProcessingError(
            'No valid data found in Excel file',
          );
          return;
        }

        setDonorResolutions({});
        dispatch({ type: 'upload_from_excel', data: roster.rows });
        notifications.fileUploadSuccess(
          `Successfully loaded ${roster.rows.length} rows`,
        );
      } catch (error) {
        console.error('Error processing Excel file:', error);
        notifications.fileProcessingError(
          error instanceof Error
            ? error.message
            : 'Failed to process Excel file',
        );
      }
    },
    [dispatch, notifications],
  );

  const showFilePicker = useCallback(() => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.xlsx,.csv';
    fileInput.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        await processFileUpload(file);
      }
    };
    fileInput.click();
  }, [processFileUpload]);

  const handleUploadClick = useCallback(() => {
    const hasData = state.rows.some(
      (row) =>
        row &&
        Object.values(row).some((val) => val !== undefined && val !== ''),
    );

    if (!hasData) {
      showFilePicker();
      return;
    }

    confirm({
      title: t('bulkDonorScheduling.uploadConfirmHeader'),
      description: t('bulkDonorScheduling.uploadConfirmMessage'),
      confirmLabel: t('common.yes'),
      cancelLabel: t('common.no'),
      onConfirm: showFilePicker,
    });
  }, [confirm, state.rows, showFilePicker, t]);

  return (
    <div className="h-[calc(100%-4rem)]">
      <UnsavedChangesGuard state={state} />

      <DonorMismatchDialog
        visible={mismatchDialog.visible}
        mismatchData={mismatchDialog.mismatchData}
        donorName={mismatchDialog.donorName}
        onUseExisting={handleUseExistingData}
        onUpdateExisting={handleUpdateExistingDonor}
        onCreateNew={handleCreateNewDonor}
        onCancel={handleCancelMismatch}
      />

      <form
        onSubmit={handleSubmit(handleValidate, handleInvalidSubmit)}
        className="mb-4 flex flex-col justify-between gap-4 lg:flex-row"
      >
        <div className="flex flex-col gap-2 md:flex-row">
          <FertilizationDropdown
            className="flex-grow"
            control={control}
            disabled={state.readonly || state.loading}
            valueChanged={() => {
              dispatch({ type: 'idle' });
            }}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {state.type === 'idle' && (
            <Button
              type="button"
              size="xl"
              className="justify-center"
              disabled={state.loading || isGridEditing}
              onClick={handleUploadClick}
            >
              {t('bulkDonorScheduling.Upload')}
            </Button>
          )}
          {state.type === 'server_validated' ||
          state.type === 'uploading' ||
          state.type === 'success' ? (
            <Button
              size="xl"
              variant="success"
              className="flex-grow justify-center"
              disabled={
                state.loading || state.type === 'success' || isGridEditing
              }
              onClick={handleUpload}
            >
              {state.loading ? (
                <>
                  <LoaderCircleIcon className="animate-spin" />
                  {t('bulkDonorScheduling.Uploading')}
                </>
              ) : (
                t('bulkDonorScheduling.Upload')
              )}
            </Button>
          ) : (
            <Button
              size="xl"
              className="justify-center"
              disabled={state.loading || isGridEditing}
              type="submit"
            >
              {state.loading ? (
                <>
                  <LoaderCircleIcon className="animate-spin" />
                  {t('bulkDonorScheduling.Validating')}
                </>
              ) : (
                t('bulkDonorScheduling.Validate')
              )}
            </Button>
          )}

          <Button
            size="xl"
            className="justify-center"
            disabled={
              state.loading || state.type === 'success' || isGridEditing
            }
            onClick={() => {
              dispatch({ type: 'add_rows', count: 10 });
            }}
          >
            {t('bulkDonorScheduling.Add_Rows')}
          </Button>

          <Button
            size="xl"
            className="justify-center"
            variant="destructive"
            disabled={state.loading || isGridEditing}
            onClick={() => {
              reset();
              setDonorResolutions({});
              dispatch({ type: 'reset' });
            }}
          >
            {t('bulkDonorScheduling.Reset')}
          </Button>

          <Button
            size="xl"
            className="justify-center"
            variant="info"
            type="button"
            disabled={
              state.loading ||
              isGridEditing ||
              state.rows.length === 0 ||
              applyAgllcPrioritiesMutation.isPending
            }
            onClick={handleCalculatePriorities}
          >
            {applyAgllcPrioritiesMutation.isPending && (
              <LoaderCircleIcon className="animate-spin" />
            )}
            {t(
              'bulkDonorScheduling.Calculate_Priorities',
              'Calculate Priorities',
            )}
          </Button>
        </div>
      </form>

      <BulkDonorScheduleGrid<AgllcGridRow>
        columns={columns}
        onEditingChange={setGridEditing}
        onReady={setApi}
        onRowChanged={handleRowChanged}
        state={state}
      />
    </div>
  );
}
